import { useEffect, useRef, useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { cn } from "@/lib/utils";
import { AuthLayout } from "./AuthLayout";
import { SSOAuthButton } from "./SSOAuthButton";
import { OIDCAuthButton } from "./OIDCAuthButton";
import {
  authLinkClassName,
  authSubmitButtonClassName,
  glassFormClassName,
  glassInputClassName,
  ssoButtonClassName,
} from "./authStyles";
import {
  disableEmailPasswordAuthentication,
  googleWorkplaceDomain,
  oidcProvider,
} from "./authConfig";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";

    if (!shouldNotify || hasDisplayedRecoveryNotification.current) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    notify("crm.auth.recovery_email_sent", {
      type: "success",
      messageArgs: {
        _: "If you're a registered user, you should receive a password recovery email shortly.",
      },
    });

    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <AuthLayout
      title={translate("crm.auth.login_title", { _: "Secure Login" })}
      subtitle={translate("crm.auth.login_subtitle", { _: "Access your CRM" })}
    >
      {disableEmailPasswordAuthentication ? null : (
        <>
          <Form
            className={cn("space-y-4", glassFormClassName)}
            onSubmit={handleSubmit}
          >
            <TextInput
              label="ra.auth.email"
              source="email"
              type="email"
              validate={required()}
              inputClassName={glassInputClassName}
            />
            <TextInput
              label="ra.auth.password"
              source="password"
              type="password"
              validate={required()}
              inputClassName={glassInputClassName}
            />
            <Button
              type="submit"
              disabled={loading}
              className={authSubmitButtonClassName}
            >
              {translate("ra.auth.sign_in")}
            </Button>
          </Form>
          <div className="mt-6 text-center">
            <Link to={"/forgot-password"} className={authLinkClassName}>
              {translate("ra-supabase.auth.forgot_password", {
                _: "Forgot password?",
              })}
            </Link>
          </div>
        </>
      )}

      {oidcProvider || googleWorkplaceDomain ? (
        <div className="mt-6 space-y-4">
          {/* No divider when there is no email/password form to divide from. */}
          {disableEmailPasswordAuthentication ? null : (
            <div className="flex items-center gap-3 text-xs text-gray-300">
              <span className="h-px flex-1 bg-white/20" />
              {translate("crm.auth.or_divider", { _: "or" })}
              <span className="h-px flex-1 bg-white/20" />
            </div>
          )}
          <div className="flex flex-col gap-3">
            {oidcProvider ? (
              <OIDCAuthButton
                provider={oidcProvider}
                className={ssoButtonClassName}
              >
                {translate("crm.auth.sign_in_oidc")}
              </OIDCAuthButton>
            ) : null}
            {googleWorkplaceDomain ? (
              <SSOAuthButton
                domain={googleWorkplaceDomain}
                className={ssoButtonClassName}
              >
                {translate("crm.auth.sign_in_google_workspace", {
                  _: "Sign in with Google Workplace",
                })}
              </SSOAuthButton>
            ) : null}
          </div>
        </div>
      ) : null}
    </AuthLayout>
  );
};
